import { useState, useEffect } from "react";
import {
  GameClubCalendarTop,
  GameClubCalendarWeekdays,
  GameClubCalendarGrid,
  GameClubCalendarWrapper,
  GameClubCalendarButtonWrapper,
  GameClubCalendarButton,
} from "./style";
import { GameClubCalendarContent } from "./style";
import { GameClubCalendarHeader } from "./style";
import { GameClubCalendarContainer } from "./style";

interface GameClubCalendarProps {
  startDate: string | null;
  endDate: string | null;
  handleStartDate: (date: string) => void;
  handleEndDate: (date: string) => void;
  onClose: () => void;
}

const GameClubCalendar = ({
  startDate,
  endDate,
  handleStartDate,
  handleEndDate,
  onClose,
}: GameClubCalendarProps) => {
  const [days, setDays] = useState<number[]>([]);
  const [daysEmpty, setDaysEmpty] = useState<number[]>([]);
  const [selectedDay1, setSelectedDay1] = useState<string>("");
  const [selectedDay2, setSelectedDay2] = useState<string>("");

  const getTodayDate = () => {
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [currentMonthLabel, setCurrentMonthLabel] = useState<string>(
    getTodayDate()
  );

  const week = ["일", "월", "화", "수", "목", "금", "토"];

  const isToday = (day: number) => {
    const [curYear, curMonth] = currentMonthLabel.split("-");
    const [todayYear, todayMonth, todayDay] = getTodayDate().split("-");
    return (
      curMonth === todayMonth && curYear === todayYear && +todayDay === day
    );
  };

  const changeMonth = (offset: number) => {
    const current = new Date(currentMonthLabel);
    current.setMonth(current.getMonth() + offset);
    const newLabel = current.toISOString().split("T")[0];
    setCurrentMonthLabel(newLabel);
  };

  const formatLabelDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}년 ${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}월`;
  };

  const getDateFromDayNumber = (day: number) => {
    const current = new Date(currentMonthLabel);
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, "0");
    const dayStr = String(day).padStart(2, "0");
    return `${year}-${month}-${dayStr}`;
  };

  const handleDate = (day: number) => {
    const newDate = getDateFromDayNumber(day);

    // 같은 날을 두 번 누르면 초기화
    if (selectedDay1 === newDate) {
      setSelectedDay1("");
      setSelectedDay2("");
      return;
    }

    if (selectedDay2 === newDate) {
      setSelectedDay1("");
      setSelectedDay2("");
      return;
    }

    if (!selectedDay1) {
      // 첫 번째 날짜 선택
      setSelectedDay1(newDate);
    } else if (!selectedDay2) {
      // 두 번째 날짜 선택
      setSelectedDay2(newDate);
    } else {
      // 이미 두 날짜가 선택된 경우, 새로 시작
      setSelectedDay1(newDate);
      setSelectedDay2("");
    }
  };

  const isDateInRange = (day: number) => {
    if (!selectedDay1 || !selectedDay2) return false;
    const date = new Date(getDateFromDayNumber(day));
    const start = new Date(
      Math.min(
        new Date(selectedDay1).getTime(),
        new Date(selectedDay2).getTime()
      )
    );
    const end = new Date(
      Math.max(
        new Date(selectedDay1).getTime(),
        new Date(selectedDay2).getTime()
      )
    );

    return date > start && date < end;
  };

  const renderDays = () => {
    const current = new Date(currentMonthLabel);
    const firstDay = new Date(
      current.getFullYear(),
      current.getMonth(),
      1
    ).getDay(); // 해당 월의 1일이 무슨 요일인지
    const lastDate = new Date(
      current.getFullYear(),
      current.getMonth() + 1,
      0
    ).getDate(); // 해당 월의 마지막 날

    setDaysEmpty(Array.from({ length: firstDay }, () => 0));
    setDays(Array.from({ length: lastDate }, (_, i) => i + 1));
  };

  useEffect(() => {
    renderDays();
  }, [currentMonthLabel]);

  useEffect(() => {
    // 초기값 설정
    if (startDate) {
      setSelectedDay1(startDate);
    }
    if (endDate) {
      setSelectedDay2(endDate);
    }
  }, [startDate, endDate]);

  const resetDate = () => {
    setSelectedDay1("");
    setSelectedDay2("");
    onClose();
  };

  const confirmDate = () => {
    if (selectedDay1 && selectedDay2) {
      const date1 = new Date(selectedDay1);
      const date2 = new Date(selectedDay2);

      if (date2 < date1) {
        // 두 번째 날짜가 첫 번째보다 이전이면 순서 바꿈
        handleStartDate(selectedDay2);
        handleEndDate(selectedDay1);
      } else {
        handleStartDate(selectedDay1);
        handleEndDate(selectedDay2);
      }
    } else if (selectedDay1) {
      // 하나의 날짜만 선택된 경우
      handleStartDate(selectedDay1);
      handleEndDate("");
    } else {
      handleStartDate("");
      handleEndDate("");
    }
    onClose();
  };

  const getDayClassName = (day: number) => {
    const classes = ["day"];
    const date = getDateFromDayNumber(day);

    // 시작일인지 확인
    if (date === selectedDay1 && selectedDay1) {
      classes.push("start-date");
    }

    // 종료일인지 확인
    if (date === selectedDay2 && selectedDay2) {
      classes.push("end-date");
    }

    // 기간 내 포함되는 날짜인지 확인 (시작일, 종료일 제외)
    if (isDateInRange(day) && date !== selectedDay1 && date !== selectedDay2) {
      classes.push("in-range");
    }

    // 오늘 날짜인지 확인
    if (isToday(day)) {
      classes.push("today");
    }

    return classes.join(" ");
  };

  return (
    <GameClubCalendarWrapper>
      <GameClubCalendarContent>
        <GameClubCalendarHeader>
          <div className="title">날짜를 선택해주세요.</div>
        </GameClubCalendarHeader>

        <GameClubCalendarContainer>
          <GameClubCalendarTop>
            <div className="calendar-header">
              <div className="nav-button" onClick={() => changeMonth(-1)}>
                &#8249;
              </div>
              <div className="month-year">
                {formatLabelDate(currentMonthLabel)}
              </div>
              <div className="nav-button" onClick={() => changeMonth(1)}>
                &#8250;
              </div>
            </div>
          </GameClubCalendarTop>

          <GameClubCalendarWeekdays>
            {week.map((day) => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </GameClubCalendarWeekdays>

          <GameClubCalendarGrid>
            {daysEmpty.map((_, index) => (
              <div key={index} className="empty"></div>
            ))}
            {days.map((day) => (
              <div
                key={day}
                className={getDayClassName(day)}
                onClick={() => handleDate(day)}
              >
                {day}
              </div>
            ))}
          </GameClubCalendarGrid>
          <GameClubCalendarButtonWrapper>
            <GameClubCalendarButton
              backgroundColor="#000"
              color="#C4C4C4"
              onClick={() => resetDate()}
            >
              취소
            </GameClubCalendarButton>
            <GameClubCalendarButton
              backgroundColor="#6436E7"
              color="#fff"
              onClick={confirmDate}
            >
              확인
            </GameClubCalendarButton>
          </GameClubCalendarButtonWrapper>
        </GameClubCalendarContainer>
      </GameClubCalendarContent>
    </GameClubCalendarWrapper>
  );
};

export default GameClubCalendar;
